import React from "react";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import { darken } from "@mui/material/styles";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SettingsIcon from "@mui/icons-material/Settings";

const estadoColorMap = {
  CREADO: { label: "CREADO", color: "#90a4ae", text: "#fff" },
  VALIDADA: { label: "VALIDADA", color: "#fbc02d", text: "#000" },
  LIBERADA: { label: "LIBERADA", color: "#4caf50", text: "#fff" },
  EN_EJECUCION: { label: "EN EJECUCIÓN", color: "#1976d2", text: "#fff" },
  FINALIZADA: { label: "FINALIZADA", color: "#388e3c", text: "#fff" },
  CERRADA: { label: "CERRADA", color: "#00695c", text: "#fff" },
  CANCELADA: { label: "CANCELADA", color: "#d32f2f", text: "#fff" },
};

// Helper visual para asignar iconos y colores según la categoría maestra de la OS
const getServiceTypeBadgeConfig = (tipserid) => {
  switch (Number(tipserid)) {
    case 1: // SERVICIO DE TRANSPORTE DE PERSONAL
      return { color: "primary", icon: <DirectionsBusIcon fontSize="small" /> };
    case 3: // SERVICIO DE TAXI
      return { color: "warning", icon: <LocalTaxiIcon fontSize="small" /> };
    default: // Carga, Lavado u Otros
      return { color: "default", icon: <SettingsIcon fontSize="small" /> };
  }
};

const ServiceOrderHeaderCard = ({
  service,
  serviceId,
  user,
  onChangeEstado,
  permissions = {},
  onManageCollaborators,
}) => {
  const gerenciaID = Number(process.env.REACT_APP_ROL_GERENTE_ID);
  const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);

  const canChangeServiceState =
    permissions.canChangeServiceState !== false &&
    typeof onChangeEstado === "function";

  const canEditingServiceCollaborator =
    permissions.canViewFinancialData !== false;

  const getAvailableTransitions = () => {
    if (!canChangeServiceState) return [];

    const current = service?.ordsercabest;
    const isPermanente = service?.serproper;
    const userRole = Number(user?.RolID);
    const transitions = [];

    if (current === "CREADO") {
      if (userRole === gerenciaID && !isPermanente) {
        transitions.push({ label: "Validar", next: "VALIDADA" });
      }

      if (userRole === administradorID && isPermanente) {
        transitions.push({ label: "Liberar", next: "LIBERADA" });
      }
    }

    if (current === "VALIDADA" && userRole === administradorID) {
      transitions.push({ label: "Liberar", next: "LIBERADA" });
    }

    if (
      ["CREADO", "VALIDADA", "LIBERADA", "EN_EJECUCION"].includes(current) &&
      userRole === gerenciaID
    ) {
      transitions.push({ label: "Cancelar", next: "CANCELADA" });
    }

    return transitions;
  };

  const transitions = getAvailableTransitions();
  const currentColor = estadoColorMap[service?.ordsercabest];

  // 🚖 Obtenemos la configuración visual correspondiente al tipo de servicio estructural
  const badgeConfig = getServiceTypeBadgeConfig(service?.tipserid);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
            <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ lineHeight: 1 }}>
              Orden de servicio
            </Typography>

            {/* 📌 CHIP INDICADOR: Resalta de forma clara la naturaleza del servicio en la cabecera */}
            {service?.tipsernom && (
              <Chip
                icon={badgeConfig.icon}
                label={service.tipsernom}
                size="small"
                color={badgeConfig.color}
                variant="filled"
                sx={{ fontWeight: 700, fontSize: "0.75rem", height: 20 }}
              />
            )}
          </Box>

          <Typography variant="h5" fontWeight={800}>
            OS-{String(serviceId).padStart(6, "0")} - {service?.ordsercabnom}
          </Typography>

          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {service?.pronom} · {service?.serpronom}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={currentColor?.label || service?.ordsercabest}
            sx={{
              bgcolor: currentColor?.color || "grey.500",
              color: currentColor?.text || "#fff",
              fontWeight: 800,
            }}
          />

          {canChangeServiceState && transitions.length === 1 && (
            <Button
              variant="contained"
              onClick={() => onChangeEstado(service?.ordsercabid, transitions[0].next)}
              sx={{
                bgcolor: estadoColorMap[transitions[0].next]?.color,
                color: estadoColorMap[transitions[0].next]?.text,
                fontWeight: 800,
                "&:hover": {
                  bgcolor: darken(
                    estadoColorMap[transitions[0].next]?.color || "#1976d2",
                    0.15
                  ),
                },
              }}
            >
              {transitions[0].label}
            </Button>
          )}

          {canEditingServiceCollaborator && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onManageCollaborators}
            >
              Gestionar Empresas
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ServiceOrderHeaderCard;